// localizedDate — a date or a time, in the session language:
// `el.call('localizedDate', value, options)`. `options` are the ordinary
// `Intl.DateTimeFormat` options (`{ month: 'short', day: 'numeric' }`,
// `{ hour: 'numeric', minute: '2-digit' }`, `timeZone`, …).
//
// Why a helper and not `toLocaleDateString` (measured live 2026-09-21, Georgian
// session): every date with a month or weekday NAME read English. Two causes:
//
//   1. about 100 call sites format with `'en-US'` or `undefined`, so the app
//      language never reaches them;
//   2. Chrome ships NO Georgian locale data. `Intl.DateTimeFormat
//      .supportedLocalesOf(['ka'])` answers `[]` and the formatter silently
//      resolves to `en-GB` — a call site that DOES pass the app language still
//      prints English. node and bun carry full ICU, so a test that only asks
//      Intl reads perfect Georgian. Same trap as `timeAgo`.
//
// So: where the runtime has the language, Intl does all the work. Where it has
// not, this keeps the numeric parts and the ORDER of `en-GB` — day, month, year,
// 24 h, which is Georgian's own order — and takes only the month and weekday
// NAMES from the bundle (`common.monthLong.*`, `common.monthShort.*`,
// `common.weekdayLong.*`, `common.weekdayShort.*`; the Georgian values are
// ICU's own, held to that by the test). A language with neither locale data
// nor bundle names degrades to the English part, never to a key string.
//
// The month and weekday INDEX are read in the requested `timeZone`: 22:30 UTC
// on 31 August is already Tuesday 1 September in Tbilisi.
//
// Self-contained on purpose (no module-scope tables or helpers): an exported
// function is serialized alone for platform delivery, and a free variable that
// is not its own would resolve to nothing there.
export function localizedDate(value, options) {
  if (value === null || value === undefined || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return date.toISOString()

  const opts = options || {}
  const lang = (this && this.call && this.call('getActiveLang')) || 'en'
  let supported = false
  try {
    supported = Intl.DateTimeFormat.supportedLocalesOf([lang]).length > 0
  } catch (_) {
    supported = false
  }
  if (supported) {
    // The LANGUAGE comes from the session, the REGION from the browser when
    // the two agree on the language. A bare `en` formats as en-US, so a
    // British reader of an English session got `Aug 19` and a 12-hour clock
    // where the browser's own `en-GB` says `19 Aug` and 16:05. A session
    // language that already names a region (`pt-BR`) is taken as written.
    let locale = lang
    try {
      const prefs =
        typeof navigator !== 'undefined' && navigator
          ? navigator.languages || (navigator.language ? [navigator.language] : [])
          : []
      const base = String(lang).toLowerCase()
      if (base.indexOf('-') === -1) {
        for (const pref of prefs) {
          if (typeof pref !== 'string') continue
          if (pref.toLowerCase().split('-')[0] !== base) continue
          if (Intl.DateTimeFormat.supportedLocalesOf([pref]).length > 0) locale = pref
          break
        }
      }
    } catch (_) {
      locale = lang
    }
    return new Intl.DateTimeFormat(locale, opts).format(date)
  }

  // No locale data for this language in this runtime.
  // LITERAL keys, written out (docs/I18N-CONVENTION §1.3: a key assembled at
  // run time is invisible to grep, to the parity test and to every extraction
  // tool — an "unused key" sweep would delete the month names).
  const MONTH_LONG = [
    'common.monthLong.jan',
    'common.monthLong.feb',
    'common.monthLong.mar',
    'common.monthLong.apr',
    'common.monthLong.may',
    'common.monthLong.jun',
    'common.monthLong.jul',
    'common.monthLong.aug',
    'common.monthLong.sep',
    'common.monthLong.oct',
    'common.monthLong.nov',
    'common.monthLong.dec'
  ]
  const MONTH_SHORT = [
    'common.monthShort.jan',
    'common.monthShort.feb',
    'common.monthShort.mar',
    'common.monthShort.apr',
    'common.monthShort.may',
    'common.monthShort.jun',
    'common.monthShort.jul',
    'common.monthShort.aug',
    'common.monthShort.sep',
    'common.monthShort.oct',
    'common.monthShort.nov',
    'common.monthShort.dec'
  ]
  const DAY_LONG = [
    'common.weekdayLong.sun',
    'common.weekdayLong.mon',
    'common.weekdayLong.tue',
    'common.weekdayLong.wed',
    'common.weekdayLong.thu',
    'common.weekdayLong.fri',
    'common.weekdayLong.sat'
  ]
  const DAY_SHORT = [
    'common.weekdayShort.sun',
    'common.weekdayShort.mon',
    'common.weekdayShort.tue',
    'common.weekdayShort.wed',
    'common.weekdayShort.thu',
    'common.weekdayShort.fri',
    'common.weekdayShort.sat'
  ]
  const zone = opts.timeZone ? { timeZone: opts.timeZone } : {}
  const named = (key, fallback) => {
    const text = this && this.call ? this.call('polyglot', key) : null
    return text && text !== key ? text : fallback
  }
  const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(date)
  return parts
    .map((part, index) => {
      if (part.type === 'month' && /^(long|short|narrow)$/.test(opts.month || '')) {
        const at = Number(
          new Intl.DateTimeFormat('en-GB', { ...zone, month: 'numeric' }).format(date)
        )
        return named((opts.month === 'long' ? MONTH_LONG : MONTH_SHORT)[at - 1], part.value)
      }
      if (part.type === 'weekday') {
        const en = new Intl.DateTimeFormat('en-GB', { ...zone, weekday: 'short' }).format(date)
        const at = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(en)
        return at < 0
          ? part.value
          : named((opts.weekday === 'long' ? DAY_LONG : DAY_SHORT)[at], part.value)
      }
      if (part.type === 'literal') {
        // en-GB writes `Wed 19 Aug`; most languages, Georgian among them, put a
        // comma after the weekday.
        if (part.value === ' ' && index > 0 && parts[index - 1].type === 'weekday') return ', '
        // Newer CLDR joins a date and a time with the WORD ` at ` (`19 Aug at
        // 16:05`; measured in bun, not in node 22). A word in a separator is
        // English too — Georgian writes a comma there.
        if (/[A-Za-z]/.test(part.value)) return ', '
      }
      return part.value
    })
    .join('')
}
